import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DialogRef } from '@ngneat/dialog';
import { MediaPlatformService } from '@outlaw/core';
import { IconComponent } from '@outlaw/icons';
import { map } from 'rxjs';
import { AddPostTab, CreateCommunityPost } from '../../interfaces/create-post';
import { PostService } from '../../services/api/post.service';
import { MessageDialogService } from '../../services/dialog/message-dialog.service';
import { SnackbarService } from '../../services/util/snackbar.service';
import { isValidPostLink, urlValidator } from '../../utils/post-link.util';
import { DialogWrapperComponent } from '../dialogs/dialog-wrapper/dialog-wrapper.component';
import { SelectionsMenuComponent } from '../selections-menu/selections-menu.component';
import { AddPostStateService } from './add-post-state.service';
import { PostTabComponent } from './post-tab/post-tab.component';
import { VideoTabComponent } from './video-tab/video-tab.component';

export interface PostFormValue {
  text: string;
  linkTitle: string;
  linkUrl: string;
}

export interface VideoFormValue {
  title: string;
  description: string;
}

@Component({
  selector: 'outlaw-add-post',
  templateUrl: './add-post.component.html',
  styleUrls: ['./add-post.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, DialogWrapperComponent, PostTabComponent, VideoTabComponent, ReactiveFormsModule, IconComponent, SelectionsMenuComponent],
  providers: [AddPostStateService],
})
export class AddPostComponent {
  #dialogRef = inject(DialogRef);
  #postService = inject(PostService);
  #snackbar = inject(SnackbarService);
  #destroyRef = inject(DestroyRef);
  #messageDialogService = inject(MessageDialogService);
  #mediaPlatformService = inject(MediaPlatformService);
  #publishLock = signal(false);

  isMobile = this.#mediaPlatformService.isMobile;
  state = inject(AddPostStateService);
  activeTab = signal<AddPostTab>('post');

  postForm = new FormGroup({
    text: new FormControl('', { nonNullable: true }),
    linkTitle: new FormControl('', { nonNullable: true }),
    linkUrl: new FormControl('', { nonNullable: true, validators: [urlValidator()] }),
  });

  #postFormValue = toSignal(this.postForm.valueChanges, {
    initialValue: this.postForm.getRawValue(),
  });

  videoForm = new FormGroup({
    title: new FormControl('', { nonNullable: true }),
    description: new FormControl('', { nonNullable: true }),
  });

  #videoFormValue = toSignal(this.videoForm.valueChanges, {
    initialValue: this.videoForm.getRawValue(),
  });

  isDirty = computed(() =>
    (this.#postFormValue().text?.trim().length ?? 0) > 0
    || (this.#videoFormValue().title?.trim().length ?? 0) > 0
    || (this.#videoFormValue().description?.trim().length ?? 0) > 0
    || this.state.hasDirtyContent()
  );

  canPublish = computed(() => {
    if (this.activeTab() === 'video') {
      return this.state.videoUploadStatus() === 'ready'
        && !!this.state.videoStreamUrl()
        && !this.state.isUploadingThumbnail();
    }
    this.#postFormValue();
    const text = this.postForm.getRawValue().text.trim();
    return (text.length > 0 || this.state.images().length > 0 || this.state.links().length > 0)
      && !this.state.isPublishing() && !this.state.isUploading();
  });

  constructor() {
    this.#dialogRef.beforeClose((result) => {
      if (result) return true;
      if (!this.isDirty()) return true;

      const confirmRef = this.#messageDialogService.open({
        message: 'Are you sure you want to close? Your changes will be lost.',
        confirmButtonText: 'Close',
        cancelButtonText: 'Cancel',
      });

      return confirmRef.afterClosed$.pipe(map(r => !!r));
    });
  }

  close(): void {
    this.#dialogRef.close();
  }

  selectTab(tab: AddPostTab): void {
    this.activeTab.set(tab);
  }

  publish(): void {
    if (this.#publishLock()) return;

    if (this.activeTab() === 'post') {
      this.#publishPost();
    } else {
      this.#publishVideo();
    }
  }

  #publishPost(): void {
    const text = this.postForm.getRawValue().text.trim();
    const hasContent = text.length > 0 || this.state.images().length > 0 || this.state.links().length > 0;
    if (!hasContent) return;

    this.#publishLock.set(true);
    this.state.isPublishing.set(true);

    const allLinks = [...this.state.links()];
    const { linkUrl, linkTitle } = this.postForm.getRawValue();
    if (linkUrl.trim() && isValidPostLink(linkUrl.trim())) {
      allLinks.push({ link_url: linkUrl.trim(), link_text: linkTitle.trim() || linkUrl.trim() });
    }

    const payload: CreateCommunityPost = {
      content: {
        title: '',
        text,
        images: this.state.images().length > 0 ? this.state.images() : undefined,
        links: allLinks.length > 0 ? allLinks : undefined,
      },
      original_platform: 'community',
      post_type: 'post',
    };

    this.#doPublish(payload);
  }

  #publishVideo(): void {
    const streamUrl = this.state.videoStreamUrl();
    if (!streamUrl) return;

    this.#publishLock.set(true);
    this.state.isPublishing.set(true);

    const { title, description } = this.videoForm.getRawValue();

    const payload: CreateCommunityPost = {
      content: {
        title: title.trim(),
        text: description.trim(),
        videos: [{
          video: streamUrl,
          video_url: streamUrl,
          ...(this.state.videoThumbnailUrl() ? {
            preview_id: this.state.videoThumbnailId(),
            preview_url: this.state.videoThumbnailUrl(),
          } : {}),
        }],
      },
      original_platform: 'community',
      post_type: 'posted_video',
    };

    this.#doPublish(payload);
  }

  #doPublish(payload: CreateCommunityPost): void {
    this.#postService.createPost(payload)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (result) => {
          this.state.isPublishing.set(false);
          this.#publishLock.set(false);
          this.state.resetVideo();
          this.state.flushSelections(result.post_uuid);
          this.#dialogRef.close(result);
        },
        error: () => {
          this.state.isPublishing.set(false);
          this.#publishLock.set(false);
          this.#snackbar.show({ message: 'Failed to publish post. Please try again.' });
        },
      });
  }
}
